import React from 'react'

import { useStore } from 'jotai'

import { EDGE_INSET } from '@/constants/insets'
import { dispatch as dispatchEvents } from '@/events/atoms'
import { uiSet } from '@/events/builders'
import { hoverSuppression } from '@/state/hoverSuppression'
import { ShortcutHint, usePageShortcut } from './shortcuts'

/**
 * A band: a row of items in a channel along a screen edge (first the editor's palette, then every
 * page's top bar; owner, 2026-10-14: "the palette tray should perhaps channel upwards at the sides?
 * i don't think we need to 'squeeze' the items as they move off. i think we should stop at the ends,
 * so that there is a notion of 'beginning' and 'end'"; then, 2026-10-15: "we should also reuse the
 * main top palette / tray for pages").
 *
 * The tray lies along the top edge of the screen (or the bottom, on a narrow screen). When there are
 * more items than fit, its two ends bend up into channels that run off the screen edge, like a
 * gutter: items ride along the tray and up the channels at full size, and the screen edge cuts them
 * off. A partly visible item in a channel is the cue that more is there; an empty channel means that
 * end of the list.
 *
 *   path distance d:  < 0  the left channel (bending up, then straight off the screen)
 *                     0..L the straight run along the tray
 *                     > L  the right channel
 *
 * Scrolling is linear and clamped between the first item and the last. A drag follows the pointer
 * exactly (past a 6 px threshold; under it a press is a tap), with a little resistance past either
 * end; the wheel (either axis) and two-finger trackpad scroll move it too. On release past an end it
 * returns with a short CSS transition: no momentum and no animation loop. When everything fits there
 * are no channels: a plain pill that does not scroll.
 *
 * Size: everything is laid out in "design" px at full size (`metrics`) and drawn at scale `k`. At
 * rest the band is `metrics.rest` thick; it grows to `metrics.grown` (full size unless given) while
 * you use it (hover, a press or drag, the wheel) and while `held`, then settles back. Growing keeps
 * the point under the pointer where it is. With `rest` equal to `tray` it never grows. `EXPAND` picks how hovering with a mouse
 * grows it: the whole tray, or only the items near the pointer (dock style).
 *
 * Items: each is a node at `metrics.item` px square. An item with `onTap` is drawn as the band's own
 * button; one without is a plain holder for a control that is its own button (a page's WorldButton,
 * which keeps its own look, shortcut and hint). A drag that started on an item never taps it.
 *
 * Selected: `selected` (an item index), or, when not given, the item holding a pressed control
 * (`aria-pressed="true"`, as WorldButton marks itself). The band keeps it on the straight run when it
 * changes (a number key, a tap in a channel) and marks it: `mark="pill"`, a short ink pill on the
 * tray under it (the editor; lifted items also grow a little), or `mark="backing"`, the bar's sliding
 * backing (a darker round shape behind it, as WorldBar draws it).
 *
 * In use (`scrim`): while grown or in the 'add' variant the scene steps back (a light dim, no blur,
 * no brush hover preview) and a press on it only puts the band away (`onDismiss`), consumed.
 */

/** How a mouse hover grows the band: 'whole' (the tray and every item) or 'dock' (items near the pointer). */
const EXPAND: 'whole' | 'dock' = 'whole'

export type BandMetrics = {
  /** An item's box at full size (px). */
  item: number
  /** Distance between items along the row at full size (px). */
  pitch: number
  /** The tray's thickness at full size (px): an item and its padding. */
  tray: number
  /** The tray's thickness at rest (px, ≤ `tray`); equal to `tray`: it never grows. */
  rest: number
  /** The tray's thickness when grown (px, between `rest` and `tray`); default `tray` (full size). */
  grown?: number
  /**
   * Radius of the bend where the tray turns up into a channel (along the items' centre line). The
   * band's inner edge curves at bend − half the tray's thickness: generous, so the scene showing
   * through inside the curve reads as a soft scoop rather than a thin dark slot.
   */
  bend: number
}

/** Horizontal room a channel takes past the straight run: the bend and half the tray's thickness. */
export const bandChannelWidth = (m: BandMetrics) => m.bend + m.tray / 2
/** Padding at each end of a plain (non-scrolling) pill. */
export const BAND_PILL_PAD = 10
/** A plain pill's length for `n` items at full size (px). */
export const bandPillWidth = (m: BandMetrics, n: number) => n * m.pitch + 2 * BAND_PILL_PAD

/** A press that moves less than this is a tap. */
const TAP_SLOP = 6
/** Past an end a drag follows this fraction of the overshoot at first, tightening towards RUBBER_MAX (design px). */
const RUBBER = 0.35
const RUBBER_MAX = 90
/** How far the wheel overshoots an end before it springs back (design px). */
const WHEEL_BOUNCE = 14
/** The spring back from past an end: the one after-release motion, a little bouncy. */
const BOUNCE = 'transform 260ms cubic-bezier(0.34, 1.56, 0.64, 1)'
/** How long it stays grown after a touch is lifted, and after the last wheel event (ms). */
const LINGER_TOUCH = 500
const LINGER_WHEEL = 600
const MS = 150
const EASE = 'cubic-bezier(0.2, 0.8, 0.3, 1)'
const INK = '16, 32, 48'
/** The 'add' variant's colours: the dashed rim (a quiet mid grey) and the band's tint (a cool grey). */
export const BAND_ADD_ACCENT = '#9aa5ae'
const ADD_TINT = '#eef1f4'

export type BandRegion = {
  /** The band of the screen the row may use (px from the left). */
  left: number
  right: number
  /** The screen edge it lies along: the channels run off this edge. */
  edge: 'top' | 'bottom'
}

export type BandItem = {
  key: React.Key
  node: React.ReactNode
  /** Drawn as the band's own button, tapped with this; without it the node is its own control. */
  onTap?: () => void
  /** The band's button's label. */
  label?: string
  /** The band's button's pressed state (aria). */
  pressed?: boolean
  /** Selected and current: a little larger and raised off the mark (the editor's palette). */
  lifted?: boolean
  /** A key that taps the item (the band registers it and shows its hint on the straight run). */
  shortcut?: string
  /** Stays upright as it rides a channel (a "+" tilted reads as a ×). */
  upright?: boolean
}

/** A point on the path in design px: `x` along, `up` towards the screen edge, `angle` (rad). */
type Pose = { x: number; up: number; angle: number }

/** The path: a straight run from `xL` to `xR`, bending up at each end into a channel (when `channels`). */
function pathPose(d: number, xL: number, xR: number, channels: boolean, bend: number): Pose {
  const L = xR - xL
  if (!channels || (d >= 0 && d <= L)) return { x: xL + d, up: 0, angle: 0 }
  const arc = (Math.PI / 2) * bend
  const e = d > L ? d - L : -d
  const side = d > L ? 1 : -1
  const base = d > L ? xR : xL
  if (e <= arc) {
    const th = e / bend
    return { x: base + side * bend * Math.sin(th), up: bend * (1 - Math.cos(th)), angle: -side * th }
  }
  return { x: base + side * bend, up: bend + (e - arc), angle: -side * (Math.PI / 2) }
}

/** One item's key (a hook per key, so the count of keys can change). */
function BandKey({ k, run }: { k: string; run: () => void }) {
  usePageShortcut(k, run)
  return null
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

export function Band({
  items, metrics, region, selected, active = true, mark = 'pill', markColor, background = '#ffffff', variant = 'plain',
  resetKey, held = false, grow: grows = true, scrim = true, onClose, onDismiss, invalidate, hoverKey = 'band', rootProps,
}: {
  items: readonly BandItem[]
  metrics: BandMetrics
  region: BandRegion
  /** The selected item's index, or −1; undefined: the item holding a pressed control. */
  selected?: number
  /** The selection is current: marked strongly; else only a faint mark (`mark="pill"`). */
  active?: boolean
  mark?: 'pill' | 'backing' | 'none'
  /** The backing's colour (`mark="backing"`). */
  markColor?: string
  /** The tray's colour. */
  background?: string
  /** 'add': tinted, with a dashed rim (the editor's add mode). */
  variant?: 'plain' | 'add'
  /** A change starts the row from its beginning (a switch of mode). */
  resetKey?: unknown
  /** Stay grown (a card opened from the band is up). */
  held?: boolean
  /** Grow to full size while in use (false: it stays at its rest size). */
  grow?: boolean
  /** Step the scene back while the band is in use (see above). */
  scrim?: boolean
  /** When set, Escape or a press anywhere outside the band calls it (leave a mode). */
  onClose?: () => void
  /** A press on the scene while the band is in use: close what it opened (the press is consumed). */
  onDismiss?: () => void
  /** Draw a frame (the scene's hover preview is cleared when the band comes into use). */
  invalidate?: () => void
  /** The hover-suppression key while in use. */
  hoverKey?: string
  /** Extra attributes for the band's root (e.g. a data attribute). */
  rootProps?: Record<string, string>
}) {
  const { item: ITEM, pitch: PITCH, tray: TRAY, bend: BEND } = metrics
  const REST_K = Math.min(1, metrics.rest / TRAY)
  const GROWN_K = clamp((metrics.grown ?? TRAY) / TRAY, REST_K, 1)
  const CHANNEL_W = bandChannelWidth(metrics)
  const PILL_PAD = BAND_PILL_PAD

  // Grown while hovered, pressed, wheeled (until the linger runs out) or held.
  const [hover, setHover] = React.useState(false)
  const [pressing, setPressing] = React.useState(false)
  const [lingering, setLingering] = React.useState(false)
  const [live, setLive] = React.useState(false)
  // Springing back from past an end (the bouncy transition), until it has settled.
  const [bouncing, setBouncing] = React.useState(false)
  const bounceTimer = React.useRef<number | undefined>(undefined)
  const springBack = (to: number) => {
    window.clearTimeout(bounceTimer.current)
    setBouncing(true)
    setLive(false)
    scrollTo(to)
    bounceTimer.current = window.setTimeout(() => setBouncing(false), 300)
  }
  const [pointerX, setPointerX] = React.useState<number | null>(null) // dock style: the hovering mouse (px in the band)
  const grown = held || pressing || lingering || (hover && EXPAND === 'whole')

  // The selection: given, or found from the items' pressed controls (after each render).
  const itemRefs = React.useRef<(HTMLElement | null)[]>([])
  const [found, setFound] = React.useState(-1)
  React.useLayoutEffect(() => {
    if (selected !== undefined) return
    const i = itemRefs.current.findIndex((el, j) => j < items.length && !!el?.querySelector('[aria-pressed="true"]'))
    if (i !== found) setFound(i)
  })
  const sel = selected ?? found

  const C = items.length
  const room = Math.max(0, region.right - region.left)
  const plainW = C * PITCH + 2 * PILL_PAD
  // Grown size: full size, except that a row that fits at rest grows only as far as it still fits
  // (it stays a pill rather than turning into a scrolling gutter under the pointer).
  const kGrown = !grows ? REST_K : plainW * REST_K <= room ? Math.max(REST_K, Math.min(GROWN_K, room / plainW)) : GROWN_K
  const k = grown ? kGrown : REST_K
  const roomD = room / k // the band in design px
  const channels = plainW > roomD
  // The tray in design px: a pill sized to its items and centred, or the whole band with channels.
  const trayW = channels ? roomD : plainW
  const trayX = (roomD - trayW) / 2
  const xL = trayX + (channels ? CHANNEL_W : PILL_PAD)
  const xR = trayX + trayW - (channels ? CHANNEL_W : PILL_PAD)
  const L = xR - xL
  // Item j sits at path distance first + j·pitch − scroll; scroll runs from 0 (the first item in the
  // first slot) to maxScroll (the last item in the last slot).
  const first = PITCH / 2
  const maxScroll = channels ? Math.max(0, (C - 1) * PITCH - (L - PITCH)) : 0
  const top = region.edge === 'top'
  const centre = TRAY / 2
  /** Up from the centre line at which an item is wholly past the screen edge (design px). */
  const gone = centre + EDGE_INSET / k + ITEM / 2

  const [scroll, setScroll] = React.useState(0)
  const scrollRef = React.useRef(scroll)
  scrollRef.current = scroll
  const scrollTo = React.useCallback((next: number) => {
    scrollRef.current = next
    setScroll(next)
  }, [])
  const geo = React.useRef({ k, maxScroll })
  geo.current = { k, maxScroll }
  // Keep within the ends when the row, the screen or the size changes (unless a drag is past one).
  React.useEffect(() => {
    if (drag.current) return
    if (scrollRef.current > maxScroll || scrollRef.current < 0) scrollTo(clamp(scrollRef.current, 0, maxScroll))
  }, [maxScroll, scrollTo])

  /** maxScroll at scale `k1` (the band is wider in design px when smaller). */
  const maxScrollAt = (k1: number) => {
    const rd = room / k1
    if (plainW <= rd) return 0
    return Math.max(0, (C - 1) * PITCH - (rd - 2 * CHANNEL_W - PITCH))
  }

  /*
   * Growing and shrinking. The scroll is kept in design px (a fixed pitch per item at every size, so
   * it is an item index and fraction), and every change of size keeps a point of the band fixed: the
   * content point under it is xL + (q − scroll) at both scales and the band's left edge stays put, so
   * scroll' = scroll + sx·(1/k − 1/k'). `anchorX` is the last pointer position in the band (the
   * pointer or finger stays over the same item). Growing remembers the scroll at rest; shrinking with
   * no scrolling in between returns to it exactly, so hovering in and out never drifts. After a
   * scroll while grown it shrinks around the pointer instead. Each size clamps to its own ends.
   */
  const anchorX = React.useRef(room / 2)
  const restScroll = React.useRef<number | null>(null)
  const grownScroll = React.useRef<number | null>(null)
  const prevK = React.useRef(k)
  React.useLayoutEffect(() => {
    const k0 = prevK.current
    prevK.current = k
    if (k0 === k) return
    const sx = anchorX.current
    const around = clamp(scrollRef.current + sx * (1 / k0 - 1 / k), 0, maxScrollAt(k))
    if (k > k0) {
      restScroll.current = scrollRef.current
      // A drag starting now may run past an end: do not clamp under a pressed finger.
      const next = drag.current ? scrollRef.current + sx * (1 / k0 - 1 / k) : around
      grownScroll.current = next
      scrollTo(next)
    } else {
      const untouched = restScroll.current != null && grownScroll.current === scrollRef.current
      scrollTo(untouched ? clamp(restScroll.current!, 0, maxScrollAt(k)) : around)
      restScroll.current = null
      grownScroll.current = null
    }
  })

  // Keep the selected item on the straight run when it is picked (a key, a tap in a channel): only
  // when the selection changes, not when the row grows or shrinks. A `resetKey` change starts the
  // row from its beginning (then shows the selection, e.g. a just-added item at the end).
  const shownSelected = React.useRef<number | null>(null)
  const shownReset = React.useRef(resetKey)
  React.useLayoutEffect(() => {
    if (shownReset.current === resetKey) return
    shownReset.current = resetKey
    shownSelected.current = null
    restScroll.current = null
    grownScroll.current = null
    scrollTo(0)
  }, [resetKey, scrollTo])
  React.useEffect(() => {
    if (shownSelected.current === sel) return
    shownSelected.current = sel
    if (!channels || sel < 0 || sel >= C) return
    const d = first + sel * PITCH - scrollRef.current
    const lo = PITCH / 2
    const hi = L - PITCH / 2
    if (d < lo) scrollTo(clamp(scrollRef.current - (lo - d), 0, maxScroll))
    else if (d > hi) scrollTo(clamp(scrollRef.current + (d - hi), 0, maxScroll))
  }, [sel, resetKey, channels, C, first, L, maxScroll, scrollTo, PITCH])

  // `onClose`: Escape or a press anywhere outside the band.
  const closeRef = React.useRef(onClose)
  closeRef.current = onClose
  const closable = !!onClose
  React.useEffect(() => {
    if (!closable) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current?.() }
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node | null
      if (t && rootRef.current?.contains(t)) return
      closeRef.current?.()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onDown, { capture: true })
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onDown, { capture: true })
    }
  }, [closable])

  const linger = React.useRef<number | undefined>(undefined)
  const lingerFor = (ms: number) => {
    window.clearTimeout(linger.current)
    setLingering(true)
    linger.current = window.setTimeout(() => setLingering(false), ms)
  }
  React.useEffect(() => () => window.clearTimeout(linger.current), [])

  const rootRef = React.useRef<HTMLDivElement>(null)
  const bandX = (clientX: number) => clientX - (rootRef.current?.getBoundingClientRect().left ?? 0)

  /*
   * Drag: past the slop the row follows the pointer exactly (with resistance past the ends). Seen in
   * the capture phase, so a press that starts on an item's own control (which stops its press from
   * going further) still drags the row; the bubble phase stops it reaching the scene.
   */
  const drag = React.useRef<{ id: number; x0: number; y0: number; s0: number; moved: boolean; mouse: boolean; scrub: boolean } | null>(null)
  const suppressClick = React.useRef(false)
  const stop = (e: React.SyntheticEvent) => e.stopPropagation()
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    suppressClick.current = false
    const mouse = e.pointerType === 'mouse'
    if (mouse && e.button !== 0) return
    // Grow around the finger first (a mouse is already grown by its hover in the 'whole' style).
    anchorX.current = bandX(e.clientX)
    // A press on a button that sizes its tool (`WorldButton` `scrub`): up and down are its own.
    const scrub = e.target instanceof Element && !!e.target.closest('[data-scrub]')
    drag.current = { id: e.pointerId, x0: e.clientX, y0: e.clientY, s0: 0, moved: false, mouse, scrub }
    setPressing(true)
  }
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current
    if (!d || d.id !== e.pointerId || !channels) return
    const dx = e.clientX - d.x0
    if (!d.moved) {
      const dy = e.clientY - d.y0
      if (d.scrub && Math.abs(dy) >= TAP_SLOP && Math.abs(dy) > Math.abs(dx)) {
        // Mostly up or down on a sizing button: the press is the button's, not the band's.
        drag.current = null
        setPressing(false)
        return
      }
      if (Math.abs(dx) < TAP_SLOP) return
      d.moved = true
      // From here the row follows the finger: start from the scroll it grew to (no scroll yet).
      d.s0 = scrollRef.current
      suppressClick.current = true
      setLive(true)
      try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* ignore */ }
    }
    const raw = d.s0 - dx / geo.current.k
    const max = geo.current.maxScroll
    const over = raw < 0 ? raw : raw > max ? raw - max : 0
    // Past an end: resistance that tightens, so the row never runs far off its end.
    const give = Math.sign(over) * RUBBER_MAX * (1 - Math.exp((-RUBBER * Math.abs(over)) / RUBBER_MAX))
    scrollTo(raw - over + give)
  }
  const onPointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current
    if (!d || d.id !== e.pointerId) return
    drag.current = null
    anchorX.current = bandX(e.clientX)
    setPressing(false)
    const end = clamp(scrollRef.current, 0, maxScroll)
    if (end !== scrollRef.current) springBack(end)
    else setLive(false)
    if (!d.mouse) lingerFor(LINGER_TOUCH)
  }
  /*
   * Hover, with hysteresis (a pointer parked where one size's shape ends and the other's begins made
   * it grow and shrink forever). Decided from the layout, never from elements that move as it grows:
   * it starts over the compact band's area and ends only when the pointer has been outside the
   * grown band's whole area (the union of both sizes, the scoop and the strip to the screen edge
   * included). The two areas are the hysteresis, so leaving needs no delay.
   */
  const pillSpan = (k1: number): [number, number] => {
    const rd = room / k1
    const w = plainW > rd ? rd : plainW
    const x0 = ((rd - w) / 2) * k1
    return [region.left + x0, region.left + x0 + w * k1]
  }
  const zones = React.useRef({ enter: [0, 0, 0, 0], stay: [0, 0, 0, 0] })
  {
    const vh = typeof window === 'undefined' ? 0 : window.innerHeight
    const [ex0, ex1] = pillSpan(REST_K)
    const [gx0, gx1] = pillSpan(kGrown)
    const enterDepth = EDGE_INSET + TRAY * REST_K
    const stayDepth = EDGE_INSET + TRAY * GROWN_K
    const band = (depth: number): [number, number] => (top ? [0, depth] : [vh - depth, vh])
    zones.current = {
      enter: [ex0, ex1, ...band(enterDepth)],
      stay: [Math.min(ex0, gx0) - 6, Math.max(ex1, gx1) + 6, ...band(stayDepth)],
    }
  }
  const hoverRef = React.useRef(false)
  React.useEffect(() => {
    const inside = ([x0, x1, y0, y1]: number[], x: number, y: number) => x >= x0 && x <= x1 && y >= y0 && y <= y1
    const leave = () => {
      if (!hoverRef.current) return
      hoverRef.current = false
      setHover(false)
      setPointerX(null)
    }
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return
      const { enter, stay } = zones.current
      if (!hoverRef.current) {
        if (!inside(enter, e.clientX, e.clientY)) return
        hoverRef.current = true
        anchorX.current = bandX(e.clientX)
        setHover(true)
        return
      }
      if (inside(stay, e.clientX, e.clientY)) {
        if (EXPAND === 'dock') setPointerX(bandX(e.clientX))
        return
      }
      anchorX.current = bandX(e.clientX)
      leave()
    }
    const onOut = (e: PointerEvent) => { if (!e.relatedTarget) leave() } // left the window
    document.addEventListener('pointermove', onMove, { capture: true })
    document.addEventListener('pointerout', onOut)
    window.addEventListener('blur', leave)
    return () => {
      document.removeEventListener('pointermove', onMove, { capture: true })
      document.removeEventListener('pointerout', onOut)
      window.removeEventListener('blur', leave)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Wheel (either axis) and trackpad scroll, clamped at the ends; grown while it runs.
  const wheelRef = React.useRef<(e: WheelEvent) => void>(() => {})
  wheelRef.current = (e: WheelEvent) => {
    e.preventDefault()
    e.stopPropagation()
    anchorX.current = bandX(e.clientX)
    if (!grown && kGrown !== REST_K) {
      // Grow first; the wheel moves the grown row from the next event on.
      lingerFor(LINGER_WHEEL)
      return
    }
    lingerFor(LINGER_WHEEL)
    const unit = e.deltaMode === 1 ? 24 : e.deltaMode === 2 ? PITCH * 3 : 1
    const delta = (Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY) * unit
    window.clearTimeout(wheelIdle.current)
    wheelIdle.current = window.setTimeout(() => {
      setLive(false)
      wheelBounced.current = false
    }, 140)
    const max = geo.current.maxScroll
    const next = scrollRef.current + delta
    if (next >= 0 && next <= max) {
      setLive(true)
      scrollTo(next)
      return
    }
    // Into an end: once per burst, overshoot a little and spring back; further events into the end
    // in the same burst just hold it there (no fighting the wheel).
    const end = next < 0 ? 0 : max
    if (wheelBounced.current || bouncing) {
      if (!bouncing) scrollTo(end)
      return
    }
    wheelBounced.current = true
    setLive(true)
    scrollTo(end + Math.sign(next - end) * Math.min(WHEEL_BOUNCE, Math.abs(next - end) * RUBBER + 4))
    window.setTimeout(() => springBack(end), 40)
  }
  const wheelBounced = React.useRef(false)
  const wheelIdle = React.useRef<number | undefined>(undefined)
  React.useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const on = (e: WheelEvent) => wheelRef.current(e)
    el.addEventListener('wheel', on, { passive: false })
    return () => {
      window.clearTimeout(wheelIdle.current)
      el.removeEventListener('wheel', on)
    }
  }, [])

  /** True (and resets) when this click ends a drag: it must not tap anything. */
  const dragClick = () => {
    if (!suppressClick.current) return false
    suppressClick.current = false
    return true
  }

  const move = `${MS}ms ${EASE}`
  const transition = live ? `opacity ${move}` : bouncing ? `${BOUNCE}, opacity ${move}` : `transform ${move}, opacity ${move}`
  /** Design px to the container's screen px (y down), for this edge and scale. */
  const yOf = (up: number) => (top ? centre - up : centre + up)
  const place = (p: Pose, grow = 1, raise = 0) => {
    const angle = (top ? p.angle : -p.angle) * (180 / Math.PI)
    // Grown items keep their edge-side in place (they grow away from the screen edge).
    const shift = (grow - 1) * (ITEM / 2) - raise
    const y = yOf(p.up) + (top ? shift : -shift)
    return `translate(${p.x * k}px, ${y * k}px) translate(-50%, -50%) rotate(${angle}deg) scale(${k * grow})`
  }

  // The tray: a thick round-ended stroke along the path, bending up and off the screen at the ends
  // (a pill is the same path without channels). Always 28 points, so its shape can transition.
  const trayPath = (() => {
    const far = gone + TRAY
    const pts: string[] = []
    const addPt = (p: Pose) => pts.push(`${(p.x * k).toFixed(1)} ${(yOf(p.up) * k).toFixed(1)}`)
    const arc = channels ? (Math.PI / 2) * BEND : 0
    const pose = (d: number) => pathPose(d, xL, xR, channels, BEND)
    // A plain pill's stroke runs between its end items' centres (its round caps make the ends).
    const a = first
    const b = L - first
    addPt(channels ? pose(-(arc + far)) : pose(a))
    for (let i = 0; i <= 12; i++) addPt(channels ? pose(-arc + (arc * i) / 12) : pose(a))
    for (let i = 0; i <= 12; i++) addPt(channels ? pose(L + (arc * i) / 12) : pose(b))
    addPt(channels ? pose(L + arc + far) : pose(b))
    return `M ${pts.join(' L ')}`
  })()

  const selD = sel >= 0 && sel < C ? first + sel * PITCH - scroll : null
  const selPose = selD == null ? null : pathPose(selD, xL, xR, channels, BEND)
  const markShown = selPose != null && selPose.up === 0
  const edgeInset = `calc(${EDGE_INSET}px + env(safe-area-inset-${region.edge}, 0px))`
  const adding = variant === 'add'

  /*
   * While the band is in use (grown, in the 'add' variant, or a card from it open) the scene steps
   * back: a light dim over it (no blur, owner, 2026-10-22), no brush hover preview, and a press on it does not draw — it
   * only puts the band away (closes its card or mode, shrinks the band) and is consumed.
   */
  const engaged = scrim && (grown || adding)
  const store = useStore()
  const invalidateRef = React.useRef(invalidate)
  invalidateRef.current = invalidate
  React.useEffect(() => {
    if (!scrim) return
    hoverSuppression.set(hoverKey, engaged)
    if (engaged) {
      dispatchEvents(store, uiSet({ cursorHit: null }))
      invalidateRef.current?.()
    }
  }, [engaged, store, scrim, hoverKey])
  React.useEffect(() => () => hoverSuppression.set(hoverKey, false), [hoverKey])
  const dismiss = (e: React.PointerEvent) => {
    e.stopPropagation()
    e.preventDefault()
    window.clearTimeout(linger.current)
    setLingering(false)
    setPressing(false)
    // (A mode is left by its own outside-press listener, which sees this press first.)
    onDismiss?.()
  }

  return (
    <>
    {scrim && (
      <div
        aria-hidden
        data-palette-scrim=""
        onPointerDown={dismiss}
        onPointerMove={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 19,
          background: `rgba(${INK}, 0.16)`,
          opacity: engaged ? 1 : 0,
          pointerEvents: engaged ? 'auto' : 'none',
          transition: `opacity ${MS}ms ease-out`,
          touchAction: 'none',
        }}
      />
    )}
    <div
      ref={rootRef}
      data-world-bar=""
      {...rootProps}
      onPointerDownCapture={onPointerDown}
      onPointerDown={stop}
      onPointerMoveCapture={onPointerMove}
      onPointerMove={stop}
      onPointerUpCapture={onPointerEnd}
      onPointerUp={stop}
      onPointerCancelCapture={onPointerEnd}
      onPointerCancel={stop}
      style={{
        position: 'absolute',
        zIndex: 20,
        left: region.left,
        [region.edge]: edgeInset,
        width: room,
        height: TRAY * k,
        transition: `height ${move}`,
        touchAction: 'none',
        cursor: channels ? (live ? 'grabbing' : 'grab') : undefined,
        // Only the tray takes presses: the band's empty ends stay the world's.
        pointerEvents: 'none',
      }}
    >
      {items.map((item) => item.shortcut && item.onTap ? <BandKey key={item.key} k={item.shortcut} run={item.onTap} /> : null)}
      {/* The hit area: the tray's straight part (the channels' visible bits are items, which are hit themselves). */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          height: '100%',
          left: (trayX + (channels ? CHANNEL_W - TRAY / 2 : 0)) * k,
          width: (channels ? L + TRAY : trayW) * k,
          transition: `left ${move}, width ${move}`,
          pointerEvents: 'auto',
        }}
      />
      <svg
        aria-hidden
        width={room}
        height={TRAY * k}
        style={{ position: 'absolute', left: 0, top: 0, zIndex: 1, overflow: 'visible', pointerEvents: 'none', filter: `drop-shadow(0 6px 12px rgba(${INK}, 0.16))` }}
      >
        {/* The 'add' variant's rim: dashes just outside the band. */}
        <path
          fill="none"
          stroke={BAND_ADD_ACCENT}
          strokeLinecap="butt"
          strokeLinejoin="round"
          strokeDasharray={`${9 * k} ${7 * k}`}
          style={{
            d: `path("${trayPath}")`,
            strokeWidth: (TRAY + 8) * k,
            opacity: adding ? 1 : 0,
            transition: live ? `opacity ${move}` : `d ${move}, stroke-width ${move}, opacity ${move}`,
          } as React.CSSProperties}
          d={trayPath}
        />
        <path
          fill="none"
          strokeLinejoin="round"
          strokeLinecap="round"
          style={{
            d: `path("${trayPath}")`,
            stroke: adding ? ADD_TINT : background,
            strokeWidth: TRAY * k,
            transition: live ? `stroke ${move}` : `d ${move}, stroke-width ${move}, stroke ${move}`,
          } as React.CSSProperties}
          d={trayPath}
        />
      </svg>
      {/* The selected mark: a short ink pill on the tray, under the lifted item, on the screen side. */}
      {selPose && mark === 'pill' && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            zIndex: 2,
            width: 22,
            height: 5,
            borderRadius: 3,
            background: `rgba(${INK}, ${active ? 0.7 : 0.25})`,
            opacity: markShown ? 1 : 0,
            transform: `translate(${selPose.x * k}px, ${(top ? TRAY - 7 : 7) * k}px) translate(-50%, -50%)`,
            transition,
            pointerEvents: 'none',
          }}
        />
      )}
      {/* The sliding backing: a darker round shape behind the selected item, riding with it. */}
      {selPose && mark === 'backing' && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            zIndex: 2,
            width: ITEM + 14,
            height: ITEM + 14,
            borderRadius: 999,
            background: markColor ?? `rgba(${INK}, 0.2)`,
            transform: place(selPose),
            opacity: selPose.up >= gone ? 0 : 1,
            transition,
            pointerEvents: 'none',
          }}
        />
      )}
      {items.map((item, j) => {
        const p = pathPose(first + j * PITCH - scroll, xL, xR, channels, BEND)
        const isSel = j === sel
        const lifted = !!item.lifted
        const offScreen = p.up >= gone
        const onRun = p.up === 0
        // Dock style: items near the hovering mouse grow towards the grown size.
        let dock = 1
        if (EXPAND === 'dock' && pointerX != null && !grown) {
          const dx = (p.x * k - pointerX) / (PITCH * k * 1.4)
          dock = 1 + (GROWN_K / k - 1) * Math.exp(-dx * dx)
        }
        const grow = dock * (lifted ? 1.1 : 1)
        const style: React.CSSProperties = {
          position: 'absolute',
          left: 0,
          top: 0,
          zIndex: isSel ? 4 : 3,
          width: ITEM,
          height: ITEM,
          padding: 0,
          border: 'none',
          background: 'transparent',
          display: 'grid',
          placeItems: 'center',
          cursor: 'inherit',
          touchAction: 'none',
          WebkitTapHighlightColor: 'transparent',
          transformOrigin: '50% 50%',
          // Lifted: a little larger and raised off the mark.
          transform: place(item.upright ? { ...p, angle: 0 } : p, grow, lifted && onRun ? 3 : 0),
          // Past the edge: hidden too, in case a safe-area inset leaves it in sight.
          opacity: offScreen ? 0 : 1,
          transition: EXPAND === 'dock' && pointerX != null ? `transform 60ms linear, opacity ${move}` : transition,
          willChange: 'transform',
          pointerEvents: offScreen ? 'none' : 'auto',
        }
        const hint = item.shortcut && item.onTap && onRun ? <ShortcutHint shortcut={item.shortcut} /> : null
        const ref = (el: HTMLElement | null) => { itemRefs.current[j] = el }
        if (item.onTap) {
          const tap = item.onTap
          return (
            <button
              key={item.key}
              ref={ref}
              type="button"
              aria-label={item.label}
              aria-pressed={item.pressed}
              aria-hidden={offScreen || undefined}
              title={item.label}
              tabIndex={onRun ? 0 : -1}
              onClick={(e) => {
                e.stopPropagation()
                if (dragClick()) return
                tap()
              }}
              style={style}
            >
              {item.node}
              {hint}
            </button>
          )
        }
        return (
          <div
            key={item.key}
            ref={ref}
            aria-hidden={offScreen || undefined}
            onClickCapture={(e) => {
              if (!dragClick()) return
              e.stopPropagation()
              e.preventDefault()
            }}
            style={style}
          >
            {item.node}
          </div>
        )
      })}
    </div>
    </>
  )
}
